import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/app/shell/page-header";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { optionalString, KEEP_PLACE } from "@/lib/search";
import { InvoiceList } from "@/routes/_authenticated/_financials/invoice-roll/-components/invoice-list";
import {
  isStatus,
  type Status,
} from "@/routes/_authenticated/_financials/invoice-roll/-components/invoice-status";
import { InvoiceMatrix } from "@/routes/_authenticated/_financials/invoice-roll/-components/invoice-matrix";

const VIEWS = ["roll", "invoices"] as const;
type View = (typeof VIEWS)[number];
const isView = (value: unknown): value is View =>
  typeof value === "string" && (VIEWS as readonly string[]).includes(value);

type InvoiceRollSearch = {
  view?: View;
  status?: Status;
  q?: string;
  resident?: string;
  gap?: string;
};

export const Route = createFileRoute("/_authenticated/_financials/invoice-roll/")({
  validateSearch: (search: Record<string, unknown>): InvoiceRollSearch => ({
    view: isView(search.view) ? search.view : undefined,
    status: isStatus(search.status) ? search.status : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    resident: optionalString(search.resident)?.slice(0, 40) || undefined,
    gap: optionalString(search.gap)?.slice(0, 40) || undefined,
  }),
  component: InvoiceRoll,
});

/** The roll is the main view. The flat list stays because aging reports link into an invoice by id
 * and "what is still owed on this one" is not a grid question. */
function InvoiceRoll() {
  const { facilityId } = useFacility();
  const { view, status, q, resident, gap } = Route.useSearch();
  const navigate = Route.useNavigate();
  const current: View = view ?? "roll";

  return (
    <>
      <PageHeader
        title="Invoice roll"
        meta="Every month a resident should have been billed for, and every invoice raised."
      />

      <Tabs
        value={current}
        onValueChange={(next) =>
          navigate({
            search: {
              view: next === "roll" ? undefined : (next as View),
              status,
              q,
              resident,
              gap,
            },
          })
        }
      >
        <TabsList>
          <TabsTrigger value="roll">Roll</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>
      </Tabs>

      {current === "roll" ? (
        <InvoiceMatrix
          facilityId={facilityId}
          residentStatus={resident ?? "active"}
          zeroMonth={gap ?? "any"}
          onResidentStatus={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, resident: next === "active" ? undefined : next }),
              ...KEEP_PLACE,
            })
          }
          onZeroMonth={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, gap: next === "any" ? undefined : next }),
              ...KEEP_PLACE,
            })
          }
        />
      ) : (
        <InvoiceList
          facilityId={facilityId}
          status={status}
          q={q}
          navigate={(options) =>
            navigate({
              search: {
                view: "invoices" as const,
                status: options.search.status,
                q: options.search.q,
              },
              replace: options.replace,
              resetScroll: !options.replace,
            })
          }
        />
      )}
    </>
  );
}
